import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { messagesService, parseCreateThreadError, } from "@/api/services/messages.service";
import { useAuth } from "@/context/AuthContext";
import { getUserFullName } from "@/utils/formatDisplayName";
import { useToast } from "@/components/common";
import { useMessagesSignalR } from "@/hooks/useMessagesSignalR";
import { formatDateOnlyDisplay } from "@/utils/dateTime";
import styles from "./EstimatingNotificationsTab.module.css";
const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1)
        return "Just now";
    if (diffMins < 60)
        return `${diffMins} min. ago`;
    if (diffHours < 24)
        return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7)
        return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    return formatDateOnlyDisplay(dateString);
};
export const EstimatingNotificationsTab = ({ contactId, quoteId, onMarkedAsRead, }) => {
    const { user } = useAuth();
    const { error: showError, info: showInfo } = useToast();
    const [thread, setThread] = useState(null);
    const [selectedThreadId, setSelectedThreadId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageText, setMessageText] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [error, setError] = useState(null);
    const [sending, setSending] = useState(false);
    const loadQuoteConversation = useCallback(async () => {
        if (!contactId || !quoteId)
            return;
        setLoading(true);
        setError(null);
        try {
            let result = await messagesService.getThreadByQuote(quoteId, contactId);
            if (!result) {
                try {
                    const createRes = await messagesService.createThread({
                        contactId,
                        type: "QUOTE",
                        quoteId,
                    });
                    const thread = createRes.data;
                    if (thread?.id) {
                        result = {
                            thread: thread,
                            messages: [],
                        };
                    }
                }
                catch (createErr) {
                    const { message } = parseCreateThreadError(createErr);
                    setError(message);
                    showError(message);
                }
            }
            if (result) {
                setThread(result.thread);
                setSelectedThreadId(result.thread.id);
                setMessages(result.messages);
                messagesService.markAsRead(result.thread.id).then(() => onMarkedAsRead?.()).catch(() => { });
            }
            else {
                setThread(null);
                setSelectedThreadId(null);
                setMessages([]);
            }
        }
        catch (err) {
            console.error("Failed to load quote conversation:", err);
            setError("Failed to load messages");
            showError("Failed to load messages");
        }
        finally {
            setLoading(false);
            setLoadingMessages(false);
        }
    }, [contactId, quoteId, showError]);
    const refetchMessages = useCallback(async () => {
        if (!contactId || !quoteId || !selectedThreadId)
            return;
        setLoadingMessages(true);
        try {
            const result = await messagesService.getThreadByQuote(quoteId, contactId);
            if (result && result.thread.id === selectedThreadId) {
                setThread(result.thread);
                setMessages(result.messages);
            }
        }
        catch (err) {
            console.error("Failed to refetch messages:", err);
        }
        finally {
            setLoadingMessages(false);
        }
    }, [contactId, quoteId, selectedThreadId]);
    useEffect(() => {
        loadQuoteConversation();
    }, [loadQuoteConversation]);
    useMessagesSignalR(selectedThreadId, {
        onNewMessage: () => {
            refetchMessages();
            showInfo("New message received");
        },
    });
    const handleSendMessage = async () => {
        if (!messageText.trim())
            return;
        setSending(true);
        try {
            if (selectedThreadId) {
                await messagesService.sendMessage({
                    threadId: selectedThreadId,
                    content: messageText.trim(),
                    assignedcontactId: contactId,
                    senderId: user?.id,
                    senderName: getUserFullName(user, "Staff"),
                });
                setMessageText("");
                await refetchMessages();
            }
            else {
                const response = await messagesService.sendMessage({
                    content: messageText.trim(),
                    assignedcontactId: contactId,
                    contactId,
                    quoteId,
                    threadType: "quote",
                    senderId: user?.id,
                    senderName: getUserFullName(user, "Staff"),
                });
                setMessageText("");
                const newThreadId = response.data?.threadId;
                if (newThreadId) {
                    setSelectedThreadId(newThreadId);
                    const result = await messagesService.getThreadByQuote(quoteId, contactId);
                    if (result) {
                        setThread(result.thread);
                        setMessages(result.messages);
                    }
                }
            }
        }
        catch (err) {
            console.error("Failed to send message:", err);
            showError("Failed to send message. Please try again.");
        }
        finally {
            setSending(false);
        }
    };
    if (!contactId || !quoteId) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.emptyState, children: "Select a client for this estimate to view and send messages." }) }));
    }
    if (loading) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.loadingState, children: "Loading messages..." }) }));
    }
    if (error) {
        return (_jsx("div", { className: styles.container, children: _jsxs("div", { className: styles.errorState, children: [_jsx("p", { children: error }), _jsx("button", { type: "button", onClick: loadQuoteConversation, className: styles.retryBtn, children: "Retry" })] }) }));
    }
    const currentThread = thread;
    const displayMessages = selectedThreadId ? messages : [];
    return (_jsxs("div", { className: styles.container, children: [_jsx("div", { className: styles.header, children: _jsxs("h2", { className: styles.title, children: ["Messages", " ", currentThread ? `– ${currentThread.subject || "Quote"}` : "– Quote"] }) }), _jsxs("div", { className: styles.messageInputSection, children: [_jsx("textarea", { className: styles.messageInput, placeholder: "Type your message here", value: messageText, onChange: (e) => setMessageText(e.target.value), rows: 4 }), _jsx("button", { type: "button", className: styles.sendBtn, onClick: handleSendMessage, disabled: !messageText.trim() || sending, children: sending ? "Sending..." : "Send message" })] }), _jsxs("div", { className: styles.messageHistory, children: [_jsx("h3", { className: styles.historyTitle, children: "MESSAGE HISTORY" }), loadingMessages ? (_jsx("div", { className: styles.loadingState, children: "Loading..." })) : displayMessages.length === 0 ? (_jsx("div", { className: styles.emptyState, children: "No messages yet." })) : (_jsx("div", { className: styles.messagesList, children: [...displayMessages].reverse().map((msg) => (_jsxs("div", { className: styles.messageItem, children: [_jsxs("div", { className: styles.messageHeader, children: [_jsx("span", { className: styles.senderName, children: msg.senderName }), _jsx("span", { className: styles.timestamp, children: formatRelativeTime(msg.createdAt) })] }), _jsx("div", { className: styles.messageContent, children: msg.content })] }, msg.id))) }))] })] }));
};
